<script lang="ts">
  import { scheduleUndo } from '$lib/state/actionDialog.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Download, Paperclip, Trash2 } from 'lucide-svelte';
  import { dealAttachmentsApi } from '$lib/api/crm';
  import { session } from '$lib/state/session.svelte';
  import { getErrorMessage } from '$lib/api/client';
  import type { DealAttachment } from '$lib/types';

  const { dealId }: { dealId: number } = $props();

  const queryClient = useQueryClient();

  const attachmentsQuery = createQuery(() => ({
    queryKey: ['deal-attachments', dealId],
    queryFn: () => dealAttachmentsApi.list(dealId)
  }));

  // The backend returns 503 with this exact detail when config.S3Bucket is
  // unset — treated as "feature off", not an error banner.
  const notConfigured = $derived(
    attachmentsQuery.isError && getErrorMessage(attachmentsQuery.error) === 'attachments not configured'
  );

  let fileInput: HTMLInputElement | undefined = $state();
  const uploadMutation = createMutation(() => ({
    mutationFn: (file: File) => dealAttachmentsApi.upload(dealId, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['deal-attachments', dealId] })
  }));
  function onFileChosen(e: Event) {
    const file = (e.currentTarget as HTMLInputElement).files?.[0];
    if (file) uploadMutation.mutate(file);
    (e.currentTarget as HTMLInputElement).value = '';
  }

  const deleteMutation = createMutation(() => ({
    mutationFn: (id: number) => dealAttachmentsApi.remove(dealId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['deal-attachments', dealId] })
  }));

  let downloadingId = $state<number | null>(null);
  async function download(a: DealAttachment) {
    downloadingId = a.id;
    try {
      await dealAttachmentsApi.download(dealId, a);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      downloadingId = null;
    }
  }

  function remove(a: DealAttachment) {
    scheduleUndo(`Delete attachment “${a.file_name}”`, () => deleteMutation.mutate(a.id));
  }

  function canDelete(a: DealAttachment): boolean {
    if (session.me?.user.role === 'admin') return true;
    return a.user_id !== null && a.user_id === session.me?.user.id;
  }

  function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
</script>

<div class="border-t border-[#F0F0F0] pt-4">
  <div class="mb-3 flex items-center justify-between gap-2">
    <div class="text-[11px] font-semibold uppercase tracking-[.08em] text-dark-gray">Attachments</div>
    {#if !notConfigured}
      <button
        type="button"
        class="btn-secondary !h-8 !px-2.5 !text-[12px]"
        onclick={() => fileInput?.click()}
        disabled={uploadMutation.isPending}
      >
        <Paperclip class="h-3.5 w-3.5" />
        {uploadMutation.isPending ? 'Uploading…' : 'Attach file'}
      </button>
      <input type="file" class="hidden" bind:this={fileInput} onchange={onFileChosen} />
    {/if}
  </div>

  {#if notConfigured}
    <p class="text-[12.5px] text-dark-gray">Attachments not configured.</p>
  {:else if attachmentsQuery.isLoading}
    <p class="text-[12.5px] text-dark-gray">Loading attachments&hellip;</p>
  {:else if attachmentsQuery.isError}
    <p class="text-[12.5px] text-red-600">{getErrorMessage(attachmentsQuery.error)}</p>
  {:else}
    {#if uploadMutation.isError}
      <p class="mb-1.5 text-[12px] text-red-600">{getErrorMessage(uploadMutation.error)}</p>
    {/if}
    <div class="flex flex-col gap-1.5">
      {#each attachmentsQuery.data?.results ?? [] as a (a.id)}
        <div class="flex items-center gap-2.5 rounded-lg border border-[#EFEEED] bg-[#FBFBFA] px-3 py-2">
          <div class="min-w-0 flex-1">
            <div class="truncate text-[12.5px] font-semibold text-navy">{a.file_name}</div>
            <div class="text-[11px] text-dark-gray">
              {formatSize(a.size_bytes)} &middot; {a.uploader_name || 'Unknown'}
            </div>
          </div>
          <button
            type="button"
            class="btn-secondary !h-8 !w-8 shrink-0 !p-0"
            title="Download"
            disabled={downloadingId === a.id}
            onclick={() => download(a)}
          >
            <Download class="h-3.5 w-3.5" />
          </button>
          {#if canDelete(a)}
            <button
              type="button"
              class="btn-danger !h-8 !w-8 shrink-0 !p-0"
              title="Delete"
              onclick={() => remove(a)}
            >
              <Trash2 class="h-3.5 w-3.5" />
            </button>
          {/if}
        </div>
      {:else}
        <p class="text-[12.5px] text-dark-gray">No attachments yet.</p>
      {/each}
    </div>
  {/if}
</div>
